export class DataUtilities {
    public static shallowCopy<T extends object | Array<unknown>>(value: T, target: T | null = null): T {
        target ??= (Array.isArray(value) ? [] : {}) as T;
        return Object.assign(target, value);
    }

    public static deepCopyByObject<T>(value: T): T {
        if (value == null) {
            return value;
        } else if (Array.isArray(value)) {
            const newArr = [];
            for (const row of value) {
                newArr.push(this.deepCopyByObject(row));
            }
            return newArr as T;
        } else if (value instanceof Map || value instanceof Set) {
            const tmp = this.deepCopy([...value]) as Array<[unknown, unknown]>;
            const newList: Array<[unknown, unknown]> = [];
            for (const row of tmp) {
                newList.push([row[0], this.deepCopyByObject(row[1])]);
            }
            return (value instanceof Map ? new Map(newList) : new Set(newList)) as T;
        } else if (typeof value === 'object') {
            const newObj: { [key: string]: unknown } = {};
            for (const objKey of Object.keys(value)) {
                newObj[objKey] = this.deepCopyByObject((value as { [key: string]: unknown })[objKey]) as T;
            }
            return newObj as T;
        } else {
            return value;
        }
    }

    public static deepCopy<T>(value: T): T {
        return JSON.parse(JSON.stringify(value)) as T;
    }

    public static deepCompare<T>(objA: T | null, objB: T | null): boolean {
        // compare types
        if (typeof objA !== typeof objB) {
            return false;
        }

        // compare properties recursively
        if (typeof objA === 'object' && typeof objB === 'object') {
            if (Array.isArray(objA) !== Array.isArray(objB)) {
                return false;
            }
            if (Array.isArray(objA)) {
                if (!Array.isArray(objB) || objA.length !== objB.length) {
                    return false;
                }
                for (let i = 0; i < objA.length; i++) {
                    if (!DataUtilities.deepCompare(objA[i], objB[i])) {
                        return false;
                    }
                }
            } else if (objA === null || objB === null) {
                // compare against null value
                if (objA !== objB) {
                    return false;
                }
            } else {
                const keysA = Object.keys(objA);
                const keysB = Object.keys(objB);
                if (keysA.length !== keysB.length) {
                    return false;
                }

                for (const key of keysA) {
                    if (
                        !Object.hasOwn(objB, key) ||
                        !DataUtilities.deepCompare(
                            (objA as Record<string, unknown>)[key],
                            (objB as Record<string, unknown>)[key],
                        )
                    ) {
                        return false;
                    }
                }
            }
        } else if (objA !== objB) {
            // compare primitive values
            return false;
        }

        // objects are equal
        return true;
    }

    public static dataUrlToFile(dataUrl: string, filename: string): File {
        const arr = dataUrl.split(',');
        const mime = /:(.*?);/.exec(arr[0])?.[1];
        const bstr = window.atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    }

    public static dataUrlToFormData(dataUrl: string, filename: string, formDataName: string): FormData {
        const file = this.dataUrlToFile(dataUrl, filename);

        const formData = new FormData();
        formData.append(formDataName, file);

        return formData;
    }

    public static readBlobAsArrayBuffer(value: Blob): Promise<ArrayBuffer> {
        // Workaround Safari 13 not supporting arrayBuffer
        if (value.arrayBuffer) {
            return value.arrayBuffer();
        } else {
            return new Promise(resolve => {
                const fileReader = new FileReader();
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                fileReader.onload = (event): void => resolve(event.target!.result as ArrayBuffer);
                fileReader.readAsArrayBuffer(value);
            });
        }
    }

    public static buildBase64DataUrl(type: string, base64: string): string {
        return `data:${type};base64,${base64}`;
    }

    public static async fileToBase64DataUrl(file: File): Promise<string> {
        return DataUtilities.buildBase64DataUrl(
            file.type,
            DataUtilities.arrayBufferToBase64String(await DataUtilities.readBlobAsArrayBuffer(file)),
        );
    }

    public static arrayBufferToBase64String(arrayBuffer: ArrayBuffer): string {
        return window.btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
    }

    public static saveObjectAsFile(filename: string, dataObject: object): void {
        const blob = new Blob([JSON.stringify(dataObject, null, 4)], { type: 'text/json' });
        DataUtilities.saveBlobAsFile(filename, blob);
    }

    public static saveBlobAsFile(filename: string, blob: Blob): void {
        const link = document.createElement('a');

        link.download = filename;
        link.href = window.URL.createObjectURL(blob);
        link.dataset['downloadurl'] = [blob.type, link.download, link.href].join(':');

        const dummyClickEvent = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        });

        link.dispatchEvent(dummyClickEvent);
        link.remove();
    }
}
